/**
 * SHORT-STAY GUESTS ARRIVING WITHOUT A FINISHED CHECK-IN (ai-plans/stays plan
 * S3) — the dashboard's one Stays line.
 *
 * Shown only when the organisation has Stays AND the reader holds stays:read,
 * and only while the count is above zero. The window ("the next 48 hours") is
 * the incomplete_alert_hours switch, which the server sends with the count —
 * never a number written here.
 *
 * FAIL-SILENT. This is a nudge, not the record: a failed read renders nothing
 * rather than an error box on top of the analytics. The Stays page is where
 * the host acts on it.
 *
 * The link opens the NEXT arrival's stay itself (its edit window over the
 * listing's stays, where the link is sent again): "Open the stay" for one
 * guest, "Open the next arrival" and "See all in Stays" for several. Without
 * the stay's ids (an older API) it falls back to /dashboard/stays.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../../auth/permissions';
import { getStaysAttention } from '../../../../../api/stays-client';
import type { StayAttention } from '../../../../../interfaces/stays';
import { staysIsOn, useStaysEntitlement } from '../../../../hooks/use-stays-entitlement';
import { stayHref } from '../../../stays/listing/stays-listing-routes';
import { palette, radius, shadow } from '../../../../../style-utils/theme';
import { touch } from '../../../../../style-utils/media-queries';
import { Warning, WarningBody, WarningIcon, WarningText, WarningTitle } from '../dashboard-screen.styled';

export const STAYS_PATH = '/dashboard/stays';

/* The desk's InlineAction (visitor-log/shared/id.tsx) as a router link:
   underlined lagoon, 44px on touch. */
const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font: inherit;
  font-weight: 600;
  color: ${palette.lagoon};
  text-decoration: underline;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
    border-radius: ${radius.xs};
  }

  ${touch} {
    min-height: 44px;
  }
`;

/** "2 short-stay guests arriving in the next 48 hours have not finished check-in" */
export const checkinAlertTitle = (count: number, hours: number): string =>
  count === 1
    ? `1 short-stay guest arriving in the next ${hours} hours has not finished check-in`
    : `${count} short-stay guests arriving in the next ${hours} hours have not finished check-in`;

export const StaysCheckinAlert: React.FC<{ buildingId?: number | null }> = ({ buildingId = null }) => {
  const { permissions } = useAuth();
  const canRead = can(permissions, PERMISSIONS.STAYS_READ);
  const { state: ent } = useStaysEntitlement({ enabled: canRead });
  const on = canRead && staysIsOn(ent);
  const [attention, setAttention] = useState<StayAttention | null>(null);

  useEffect(() => {
    if (!on) {
      setAttention(null);

      return;
    }
    let alive = true;
    Promise.resolve()
      .then(() => getStaysAttention(buildingId))
      .then(a => {
        if (alive) setAttention(a && typeof a.count === 'number' ? a : null);
      })
      .catch(() => {
        // Fail-silent: nothing on screen.
        if (alive) setAttention(null);
      });

    return () => {
      alive = false;
    };
  }, [on, buildingId]);

  if (!on || !attention || !(attention.count > 0)) return null;
  const first = attention.first && attention.first.listing_id ? attention.first : null;
  const firstHref = first && first.listing_id ? stayHref(first.listing_id, first.reservation_id) : null;

  return (
    <Warning role="status" data-testid="stays-checkin-alert">
      <WarningIcon aria-hidden="true">!</WarningIcon>
      <WarningBody>
        <WarningTitle>{checkinAlertTitle(attention.count, attention.hours)}</WarningTitle>
        <WarningText>
          {attention.count === 1 ? 'Send them the link again from their stay.' : 'Send them the link again from each stay.'}{' '}
          {firstHref ? (
            <>
              <InlineLink to={firstHref}>{attention.count === 1 ? 'Open the stay' : 'Open the next arrival'}</InlineLink>
              {attention.count > 1 ? (
                <>
                  {' · '}
                  <InlineLink to={STAYS_PATH}>See all in Stays</InlineLink>
                </>
              ) : null}
            </>
          ) : (
            <InlineLink to={STAYS_PATH}>Open Stays</InlineLink>
          )}
        </WarningText>
      </WarningBody>
    </Warning>
  );
};
